/**
 * Mixpanel for the TV app (docs/03 §4, docs/04 §5, WP1.8).
 *
 * `init(token)` comes from `GET /config` (`mixpanelToken`): **without a token every call is a
 * no-op**, which is how the local/dev shell and every test runs. With a token the real
 * `mixpanel-browser` SDK is pulled in through a dynamic `import()` so it never lands in the
 * initial bundle (docs/04 §6, ≤ 350 KB gzip); calls made while that chunk loads are queued and
 * replayed in order.
 *
 * Events (docs/04 §5): `login`, `logout`, `tv_paired { did }`, `screen_view { screen }`,
 * `search { qLength, results }`, `queue_add { via:'tv' }`. `song_played` is server-side
 * (outbox, docs/03 §4) and must not be sent from here.
 *
 * **No PII beyond the uid.** `identify(uid)` is the only identifier that leaves the device;
 * anything that looks like a name, e-mail, photo or free-typed query is dropped from event
 * properties by {@link sanitizeProps} (`search` reports `qLength`, never `q`).
 */
export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

export interface AnalyticsEvent {
  name: string;
  props: AnalyticsProps;
  at: number;
}

export interface Analytics {
  /** True once `init` ran with a non-empty token. */
  readonly enabled: boolean;
  init(token: string, superProps?: AnalyticsProps): void;
  identify(distinctId: string): void;
  track(name: string, props?: AnalyticsProps): void;
  screenView(screen: string): void;
  reset(): void;
  /** Last events (max 50) — dev/test inspection only. */
  buffer(): readonly AnalyticsEvent[];
}

/** The slice of `mixpanel-browser` this module uses (keeps the SDK out of the type graph). */
export interface MixpanelLike {
  init(token: string, config?: Record<string, unknown>): unknown;
  register(props: Record<string, unknown>): void;
  identify(distinctId?: string): unknown;
  track(name: string, props?: Record<string, unknown>): unknown;
  reset(): void;
}

const MAX_BUFFER = 50;

/**
 * Property names that may not leave the TV. The TV never needs them: the uid is the identity
 * and every screen/queue event is keyed by ids and counts.
 */
export const PII_PROP_KEYS: readonly string[] = [
  'q',
  'query',
  'name',
  'displayName',
  'display_name',
  'email',
  'photo',
  'photoUrl',
  'photo_url',
  'title',
  'artist',
  'artists',
  'joinCode',
  'join_code',
  'token',
];

/** Drops `undefined` and anything on {@link PII_PROP_KEYS} (docs/04 §5). */
export function sanitizeProps(props: AnalyticsProps): AnalyticsProps {
  const out: AnalyticsProps = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) continue;
    if (PII_PROP_KEYS.includes(key)) continue;
    out[key] = value;
  }
  return out;
}

export type MixpanelLoader = () => Promise<MixpanelLike>;

/**
 * The **core** `mixpanel-browser` build: the package's default entry bundles session
 * recording / autocapture (rrweb, ~130 KB gzip) which a TV never uses, while the core build
 * is ~37 KB gzip and ships the same `init/register/identify/track/reset` API. Both are
 * declared in the package (`dist/mixpanel-core.cjs.d.ts`), and `analytics.test.ts` asserts
 * this deep path keeps resolving to an object with that API.
 */
export const loadMixpanel: MixpanelLoader = async () =>
  (await import('mixpanel-browser/dist/mixpanel-core.cjs.js')).default as unknown as MixpanelLike;

/** Config shared by every environment. The TV WebView has no reliable cookie jar. */
export const MIXPANEL_CONFIG = {
  persistence: 'localStorage',
  track_pageview: false,
  // The TV is not a browser session: no autocapture, no session recording, no heatmaps.
  autocapture: false,
  record_sessions_percent: 0,
  batch_requests: true,
  // No remote-settings round trip and no lazily fetched recorder bundle on a TV.
  remote_settings_mode: 'disabled',
  ip: false,
} as const;

export class TvAnalytics implements Analytics {
  enabled = false;
  private sdk: MixpanelLike | null = null;
  private loading: Promise<void> | null = null;
  private pending: Array<(mp: MixpanelLike) => void> = [];
  private distinctId: string | null = null;
  private superProps: AnalyticsProps = {};
  private events: AnalyticsEvent[] = [];
  private loader: MixpanelLoader = loadMixpanel;

  /** Test/dev seam: replaces the dynamic `import('mixpanel-browser')`. */
  setLoader(loader: MixpanelLoader): void {
    this.loader = loader;
  }

  init(token: string, superProps: AnalyticsProps = {}): void {
    this.superProps = sanitizeProps(superProps);
    if (!token) {
      // No project configured (local dev, tests): stay a buffer-only no-op.
      this.enabled = false;
      return;
    }
    if (this.enabled) return; // `/config` is fetched once per boot; ignore a re-init.
    this.enabled = true;
    const props = this.superProps;
    this.loading = this.loader()
      .then((mp) => {
        mp.init(token, { ...MIXPANEL_CONFIG });
        mp.register({ ...props });
        this.sdk = mp;
        for (const run of this.pending.splice(0)) run(mp);
      })
      .catch(() => {
        // A blocked or offline CDN must never break the app; keep buffering locally.
        this.enabled = false;
        this.pending.length = 0;
      });
  }

  private send(run: (mp: MixpanelLike) => void): void {
    if (!this.enabled) return;
    if (this.sdk) run(this.sdk);
    else this.pending.push(run);
  }

  identify(distinctId: string): void {
    this.distinctId = distinctId;
    this.send((mp) => mp.identify(distinctId));
  }

  track(name: string, props: AnalyticsProps = {}): void {
    const clean = sanitizeProps(props);
    this.events = [
      ...this.events.slice(-(MAX_BUFFER - 1)),
      {
        name,
        props: { ...this.superProps, ...clean, distinct_id: this.distinctId ?? undefined },
        at: Date.now(),
      },
    ];
    this.send((mp) => mp.track(name, clean));
  }

  screenView(screen: string): void {
    this.track('screen_view', { screen });
  }

  reset(): void {
    this.distinctId = null;
    this.events = [];
    this.send((mp) => mp.reset());
  }

  buffer(): readonly AnalyticsEvent[] {
    return this.events;
  }

  /** Resolves once the SDK chunk finished loading (tests; no-op when disabled). */
  async ready(): Promise<void> {
    await this.loading;
  }
}

export const analytics = new TvAnalytics();

declare global {
  interface Window {
    __akpAnalytics?: Analytics;
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__akpAnalytics = analytics;
}
